import { Typography } from "components/typography";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { IcbtCommunication } from "api/types";
import { useGetAllProgressForUsers } from "api/useIcbtQueries";
import {
  FeedbackModalStack,
  ModalState,
} from "components/patients/patientCard/icbt/modals/FeedbackModal/FeedbackModalStack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { unique } from "remeda";
import { styled } from "styled-components";
import {
  useCategorizedTodoCommunication,
  type Categories,
  categories as categoryList,
} from "../hooks/useCategorizedTodoCommunication";
import { useIcbtLanguage } from "../hooks/useTreatment";
import {
  FeedbackItem,
  HandlerProps,
  UserAnswersByRequestIdByUserId,
} from "./FeedbackItem";
import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { useFlag } from "@mindlercare/unleash-client";
import { useGetAllProgramsAndTreatmentsQuery } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { parseAsStringLiteral, useQueryState } from "nuqs";

const Categories = styled.div`
  display: flex;
  margin-bottom: 20px;
  gap: 8px;
`;

const Category = styled.div<{ $active: boolean }>`
  padding: 10px 20px;
  cursor: pointer;
  color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_WHITE
      : defaultTheme.COLORS.GRAY_BLACK};
  border: ${(props) =>
    props.$active
      ? `2px solid ${defaultTheme.COLORS.GRAY_BLACK}`
      : `1px solid ${defaultTheme.COLORS.GRAY_MEDIUM}`};
  background-color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_BLACK
      : defaultTheme.COLORS.GRAY_WHITE};
  border-radius: 100px;
  &:hover {
    color: ${defaultTheme.COLORS.GRAY_WHITE};
    background-color: ${defaultTheme.COLORS.GRAY_BLACK};
  }
`;

const FeedbackList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const NoFeedbackMessage = styled(Typography)`
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
`;

export const TodoFeedbackTable = ({
  icbtCommunication,
  patients,
  questionnaireCounts,
  userAnswersByRequestIdByUserId,
}: {
  icbtCommunication?: IcbtCommunication[];
  patients?: { firstName: string | null; lastName: string | null }[];
  questionnaireCounts: QuestionnaireCounts;
  userAnswersByRequestIdByUserId: UserAnswersByRequestIdByUserId;
}) => {
  const { t } = useTranslation();

  const [modalPropsState, setModalPropsState] = useState<ModalState>({
    programId: "",
    treatmentId: "",
    userId: undefined,
    icbtRequestId: "",
    icbtFeedbackDate: "",
    icbtFeedbackDateIgnored: false,
    patientName: "",
    feedbackModalOpen: false,
    assignIcbtModalOpen: false,
  });

  const [tab, setTabState] = useQueryState(
    "state",
    parseAsStringLiteral(categoryList).withDefault("today"),
  );

  const handleTabChange = (newTab: Categories) => setTabState(newTab);

  const { data: progressByUser } = useGetAllProgressForUsers(
    unique(icbtCommunication?.map((icbt) => icbt.patient_user_id) ?? []),
  );
  const language = useIcbtLanguage();

  // TODO: loading state and error handling plz
  const { data: programsAndTreatments } = useGetAllProgramsAndTreatmentsQuery(
    language.codeISO2,
  );

  const categories = {
    today: t("DUE_TODAY"),
    week: t("DUE_THIS_WEEK"),
    all: t("ALL_FEEDBACK"),
  };

  const noFeedbackMessages = {
    today: t("NO_FEEDBACK_TODAY"),
    week: t("NO_FEEDBACK_THIS_WEEK"),
    all: t("NO_FEEDBACK"),
  };

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const toBeDoneCategorized = useCategorizedTodoCommunication({
    icbtCommunication: icbtCommunication,
    patientsToIncludeInCommunication:
      (patients as RouterOutput["patients"]["getByUserIds"]) ?? [],
    includeOnlyWithResponses: false,
    ignoreFeedbackDateFeatureEnabled,
  });
  const feedbackData = toBeDoneCategorized.get(tab);

  return (
    <>
      <Categories>
        {Object.entries(categories).map(([key, label]) => (
          <Category
            key={key}
            $active={tab === key}
            onClick={() => handleTabChange(key as Categories)}
          >
            {label} {toBeDoneCategorized.get(key as Categories)?.length ?? ""}
          </Category>
        ))}
      </Categories>
      {feedbackData?.length === 0 && (
        <NoFeedbackMessage variant="body">
          {noFeedbackMessages[tab] || t("NO_FEEDBACK")}
        </NoFeedbackMessage>
      )}
      <FeedbackList>
        {feedbackData?.map((item, i) => {
          return (
            <FeedbackItem
              displayDropdownUpwards={
                feedbackData.length > 1 && feedbackData.length === i + 1
              }
              key={item.request_id}
              item={item}
              done={false} // since we are in todo they can't be done
              progress={
                progressByUser?.[item.patient_user_id]?.[item.program_id]
              }
              programsAndTreatments={programsAndTreatments}
              questionnaireCounts={questionnaireCounts}
              userAnswersByRequestIdByUserId={userAnswersByRequestIdByUserId}
              onClick={({ icbtCommunication }: HandlerProps) => {
                setModalPropsState({
                  icbtRequestId: icbtCommunication.request_id,
                  icbtFeedbackDate: icbtCommunication.feedback_date,
                  icbtFeedbackDateIgnored:
                    icbtCommunication.feedback_date_ignored_at !== null,
                  patientName: `${item.patient?.firstName} ${item.patient?.lastName}`,
                  programId: icbtCommunication.program_id,
                  treatmentId: icbtCommunication.treatment_id,
                  userId: Number(icbtCommunication.patient_user_id),
                  feedbackModalOpen: true,
                });
              }}
            />
          );
        })}
      </FeedbackList>
      <FeedbackModalStack
        modalState={modalPropsState}
        setModalState={setModalPropsState}
      />
    </>
  );
};
